"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface BillingFormProps {
  onComplete: (data: { name: string; country: string }) => void;
  prefillName?: string;
}

const countries = [
  { code: "PL", name: "Poland" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "NL", name: "Netherlands" },
  { code: "CZ", name: "Czech Republic" },
  { code: "SK", name: "Slovakia" },
  { code: "AT", name: "Austria" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "CH", name: "Switzerland" },
  { code: "BE", name: "Belgium" },
  { code: "PT", name: "Portugal" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
];

export default function BillingForm({ onComplete, prefillName = "" }: BillingFormProps) {
  const [name, setName] = useState(prefillName);
  const [country, setCountry] = useState("PL");
  const [saveInfo, setSaveInfo] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const getFieldStatus = (field: string, value: string) => {
    if (!touched[field] || !value) return "default";
    if (errors[field]) return "error";
    return "success";
  };

  const inputClass = (field: string, value: string) => {
    const status = getFieldStatus(field, value);
    const base = "w-full bg-bg-elevated text-white placeholder-gray-600 rounded-xl px-4 py-3.5 text-sm transition-all duration-300 outline-none pr-10";
    if (status === "error") return `${base} border border-red-500/60 shadow-[0_0_0_3px_rgba(239,68,68,0.08)]`;
    if (status === "success") return `${base} border border-accent-emerald/40 shadow-[0_0_0_3px_rgba(16,185,129,0.06)]`;
    return `${base} border border-white/8 focus:border-accent-crimson/40 focus:shadow-[0_0_0_3px_rgba(255,0,76,0.08)]`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, country: true });

    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Full name is required";
    if (!country) newErrors.country = "Please select a country";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    await new Promise(r => setTimeout(r, 800));
    setLoading(false);
    onComplete({ name, country });
  };

  return (
    <div className="bg-bg-card border border-white/8 rounded-3xl p-6 md:p-8">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-white mb-1">Billing Details</h2>
        <p className="text-gray-500 text-sm">Used for your invoice and order confirmation</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-5">
          {/* Full Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
              Full Name <span className="text-accent-crimson">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (touched.name) {
                    if (!e.target.value.trim()) setErrors(prev => ({ ...prev, name: "Full name is required" }));
                    else setErrors(prev => { const n = {...prev}; delete n.name; return n; });
                  }
                }}
                onBlur={(e) => {
                  setTouched(prev => ({ ...prev, name: true }));
                  if (!e.target.value.trim()) setErrors(prev => ({ ...prev, name: "Full name is required" }));
                  else setErrors(prev => { const n = {...prev}; delete n.name; return n; });
                }}
                placeholder="Jan Kowalski"
                autoComplete="name"
                className={inputClass("name", name)}
                aria-label="Full name"
              />
              {getFieldStatus("name", name) === "success" && (
                <Icon name="CheckCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-accent-emerald" />
              )}
              {getFieldStatus("name", name) === "error" && (
                <Icon name="ExclamationCircleIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-400" />
              )}
            </div>
            {errors.name && touched.name && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={12} /> {errors.name}
              </p>
            )}
          </div>

          {/* Country */}
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">
              Country <span className="text-accent-crimson">*</span>
            </label>
            <div className="relative">
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-bg-elevated text-white rounded-xl px-4 py-3.5 text-sm border border-white/8 focus:border-accent-crimson/40 focus:shadow-[0_0_0_3px_rgba(255,0,76,0.08)] outline-none transition-all duration-300 appearance-none cursor-pointer"
                aria-label="Country"
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code} className="bg-bg-elevated">
                    {c.name}
                  </option>
                ))}
              </select>
              <Icon name="ChevronDownIcon" size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* Save info */}
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative flex-shrink-0">
              <input
                type="checkbox"
                checked={saveInfo}
                onChange={(e) => setSaveInfo(e.target.checked)}
                className="sr-only"
                aria-label="Save billing information for future purchases"
              />
              <div className={`w-5 h-5 rounded-md border transition-all duration-200 flex items-center justify-center ${
                saveInfo ? "bg-accent-crimson border-accent-crimson" : "border-white/20 bg-bg-elevated group-hover:border-white/30"
              }`}>
                {saveInfo && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
              Save for future purchases
            </span>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full mt-6 flex items-center justify-center gap-2.5 bg-gradient-to-r from-accent-crimson to-accent-crimson-hover text-white font-semibold text-sm py-4 rounded-full shadow-[0_12px_32px_rgba(255,0,76,0.35)] hover:shadow-[0_16px_40px_rgba(255,0,76,0.45)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Continue to Payment</span>
              <Icon name="ArrowRightIcon" size={15} />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
